import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import type { ISubscriptionBlockApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for attaching an already-saved org payment method to a subscription
 * block (`POST /v1/subscription-block/{uuid}/attach-payment-method`). The
 * operation is idempotent — re-attaching the block's current method succeeds.
 *
 * Returns the attached method mapped to the shared {@link PaymentMethodViewDto}
 * (same mechanics as confirm-session) so the `card-updated` confirmation can
 * render without refetching. Only internal uuids are logged — never
 * `providerId` / `providerPublicId` / card digits (charter §5).
 *
 * @param port - Subscription block API port
 * @returns Use case executor
 */
export const attachPaymentMethodUseCase = (port: ISubscriptionBlockApiPort) => {
  const execute = async (blockUuid: string, paymentMethodUuid: string): Promise<PaymentMethodViewDto> => {
    const span = tracer.startSpan('SUB.attachBlockPaymentMethod', {
      attributes: { 'block.uuid': blockUuid, 'payment.uuid': paymentMethodUuid },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const paymentMethod = await port.attachPaymentMethod(blockUuid, paymentMethodUuid);
        paymentLog.info('attachBlockPaymentMethod', {
          'block.uuid': blockUuid,
          'payment.uuid': paymentMethod.uuid,
          methodType: paymentMethod.methodType,
        });
        return paymentMethod;
      } catch (error) {
        paymentLog.error('attachBlockPaymentMethod', {
          'block.uuid': blockUuid,
          'payment.uuid': paymentMethodUuid,
          ...extractErrorAttributes(error),
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
